import React, { useState, useEffect, useRef } from "react";
import { Card, Typography, Collapse, AutoComplete, Divider, Spin } from "antd";
import { useRecoilState } from "recoil";
import { DownOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { customFormat } from "../common/custom-format";
import { SAND_CAT } from "../config";
import LoadingSpinner from "../common/loading";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { developerAccessTokenState } from "../global-state";

const AssistantHistoryList = () => {
  const [groupedHistories, setGroupedHistories] = useState({});
  const [data, setData] = useState([]);
  const [expandedHistories, setExpandedHistories] = useState([]);
  const currentRequestRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const observerRef = useRef(null);
  const hasMore = useRef(true);
  const nextToken = useRef("");
  const minSize = useRef(0);
  const [developerAccessToken] = useRecoilState(developerAccessTokenState);
  const selectedDate = useRef("");
  const lastFilterTimestampRef = useRef(null);
  const pastSevenDays = useRef([]);

  useEffect(() => {
    const today = new Date();
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      dates.push(date.toISOString().split("T")[0]);
    }
    pastSevenDays.current = dates;
  }, []);

  const handleDateChange = event => {
    setGroupedHistories({});
    setData([]);
    nextToken.current = "";
    fetchData(true);
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    };

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(entries => {
      const target = entries[0];
      if (target.isIntersecting && !loading && hasMore.current) {
        fetchData(false);
      }
    }, options);

    observerRef.current = observer;

    const observerTarget = document.querySelector(".observer-target");
    if (observerTarget) {
      observer.observe(observerTarget);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!data || data.length === 0) return;
    const grouped = data.reduce((acc, History) => {
      const day = dayjs(History.timestamp).format("YYYY-MM-DD");
      if (!acc[day]) acc[day] = [];
      acc[day].push(History);
      return acc;
    }, {});
    setGroupedHistories(grouped);
  }, [data]);

  const fetchData = async (isReset = false) => {
    if (loading || (!hasMore.current && !isReset)) return;

    const filterTimestamp = selectedDate.current
      ? dayjs(selectedDate.current).endOf("day").valueOf()
      : null;

    if (
      filterTimestamp &&
      filterTimestamp === lastFilterTimestampRef.current &&
      isReset
    ) {
      return;
    }

    if (isReset) {
      minSize.current = 0;
      setGroupedHistories({});
      setData([]);
      nextToken.current = "";
      setHistoryLoading(true);
      hasMore.current = true;
    } else {
      setLoading(true);
    }

    const requestId = Symbol();
    currentRequestRef.current = requestId;

    try {
      const token = isReset ? "" : nextToken.current;
      const encodedToken = token ? encodeURIComponent(token) : null;

      const response = await fetch(
        `${SAND_CAT}/assistant/history/list?size=20&model=gemini-1.5-flash${
          encodedToken ? `&nextToken=${encodedToken}` : ""
        }${
          filterTimestamp === lastFilterTimestampRef.current
            ? ""
            : `&filterTimestamp=${filterTimestamp}`
        }`,
        {
          method: "GET",
          headers: {
            DeveloperAuthorization: `Bearer ${developerAccessToken}`,
            "Content-Type": "application/json",
          },
        },
      );
      const jsonData = await response.json();
      if (!response.ok) {
        throw new Error(jsonData);
      }

      if (currentRequestRef.current !== requestId) {
        return;
      }

      lastFilterTimestampRef.current = filterTimestamp;

      if (lastFilterTimestampRef.current !== filterTimestamp) {
        return;
      }

      let histories = [];

      if (jsonData.assistantHistories) {
        histories = jsonData.assistantHistories;
      }

      nextToken.current = jsonData.page.nextToken;
      hasMore.current = jsonData.page.hasMore;

      const sortedHistories = (histories || []).sort(
        (a, b) => b.timestamp - a.timestamp,
      );

      setData(prev =>
        isReset ? sortedHistories : [...prev, ...sortedHistories],
      );

      if (jsonData.page.hasMore && minSize.current < 20) {
        minSize.current += histories.length;
        if (minSize.current < 20 && jsonData.page.hasMore) {
          await fetchData(false);
        }
      }
    } catch (error) {
      console.error("Failed to fetch automations:", error);
    } finally {
      if (currentRequestRef.current === requestId) {
        setLoading(false);
        setHistoryLoading(false);
      }
    }
  };

  const handleToggleHistory = index => {
    setExpandedHistories(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index],
    );
  };

  return (
    <>
      <Card style={{ marginBottom: 8 }}>
        <div
          style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
            padding: 16,
            gap: 12,
            }}
          >
          <div
            style={{
                minWidth: "200px",
                maxWidth: "400px",
                height: "40px",
              }}
            >
            <AutoComplete
              options={pastSevenDays.current.map(date => ({ value: date, label: date }))}
                value={
                pastSevenDays.current.find(date => date === selectedDate.current) || null
                }
              onChange={(value) => {
                if (value) {
                  selectedDate.current = value;
                    handleDateChange();
                  }
                }}
                    placeholder="Select a date"
              style={{ width: "100%", height: "100%" }}
              filterOption={(inputValue, option) =>
                option.value.toLowerCase().includes(inputValue.toLowerCase())
              }
              />
          </div>
        </div>
        </Card>

        {historyLoading && (
        <div
          style={{
              display: "flex",
              justifyContent: "center",
            padding: "32px 0",
            }}
          >
          <Spin size="small" />
        </div>
        )}
        {!historyLoading && (
          <>
            {Object.keys(groupedHistories).length > 0 ? (
              Object.keys(groupedHistories).map((day, idx) => (
              <div key={idx} style={{ marginBottom: 32 }}>
                <Typography.Title level={5} style={{ marginBottom: 8 }}>
                    {day}
                </Typography.Title>
                <Divider style={{ marginBottom: 16 }} />

                <Collapse
                  activeKey={expandedHistories}
                  onChange={(keys) => {
                    setExpandedHistories(Array.isArray(keys) ? keys : keys ? [keys] : []);
                  }}
                  items={groupedHistories[day].map((history, index) => {
                    const panelKey = `${day}-${index}`;
                    return {
                      key: panelKey,
                      label: (
                        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                          <Typography.Text strong>task</Typography.Text>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {customFormat(
                              dayjs(history.timestamp),
                              currentTime,
                            )}
                          </Typography.Text>
                        </div>
                      ),
                      children: (
                        <>
                        {history?.task?.draftScript && (
                            <>
                            <Divider />
                              <div style={{ display: "flex", marginTop: 8 }}>
                                <div
                                  style={{
                                    marginTop: 12,
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: history?.task?.draftSuccess
                                      ? "#52c41a"
                                      : "#ff4d4f",
                                  flexShrink: 0,
                                  marginRight: "8px",
                                }}
                              />
                                <div
                                  style={{
                                  wordWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                    flex: 1,
                                }}
                              >
                                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                  {history?.task?.draftScript}
                                </ReactMarkdown>
                                </div>
                              </div>
                            </>
                        )}
                        {history?.task?.finalScript && (
                            <>
                            <Divider />
                              <div style={{ display: "flex", marginTop: 8 }}>
                                <div
                                  style={{
                                    marginTop: 12,
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: history?.task?.finalSuccess
                                      ? "#52c41a"
                                      : "#ff4d4f",
                                  flexShrink: 0,
                                  marginRight: "8px",
                                }}
                              />
                                <div
                                  style={{
                                  wordWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                    flex: 1,
                                }}
                              >
                                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                  {history?.task?.finalScript}
                                </ReactMarkdown>
                                </div>
                              </div>
                            </>
                        )}
                          {history?.task?.instructions?.map(
                            (instruction, idx) => (
                              <React.Fragment key={idx}>
                              <Divider />
                                <div
                                  style={{
                                    marginBottom: 8,
                                    marginTop: -8,
                                  wordWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                  {instruction}
                                </ReactMarkdown>
                                </div>
                            </React.Fragment>
                          ),
                        )}
                        </>
                      ),
                    };
                  })}
                />
              </div>
              ))
            ) : (
            <Typography.Text type="secondary">
                No histories available.
            </Typography.Text>
            )}
          </>
        )}
      <div
          className="observer-target"
          ref={el => {
            if (el && observerRef.current) {
              observerRef.current.observe(el);
            }
          }}
        style={{
            height: "20px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {loading && !historyLoading && <LoadingSpinner />}
      </div>
        {!loading &&
          !hasMore.current &&
          Object.keys(groupedHistories).length > 0 && (
          <Typography.Text
            type="secondary"
            style={{ display: "block", textAlign: "center", padding: 16 }}
            >
              No more activities
          </Typography.Text>
          )}
    </>
  );
};

export default AssistantHistoryList;
